import {useEffect, useState, useRef} from "react";
import CodeReader from "./CodeReader";
import axios from "axios";
import config from "./config/config";
import './style.scss';
import gfx_map from './map.png';
import gfx_bg from './background.webp';
import gfx_wristband from './wristband.png';
import gfx_location from './location.svg';
import moment from "moment";
import "moment/locale/de";

const waitingSpot = {
  mic: "Mikro",
  island: "Insel",
  keys: "Schlüssel",
  kitchen: "Küche",
  star: "Stern",
  mobile: "Mobile Zellen",
  bike: "Fahrrad",
  sofa: "Sofa",
  tent: "Zelt",
  sun: "Sonne",
  tree: "Baum",
  window: "bitte erfragen",
  turtle: "Schildkröte",
  bottle: "Flasche",
  cat: "Katze",
  hat: "Hut",
};

const JACKPOT_INTERVAL = 100;

const SHOW_SHIFTS_DURATION = 30000;

const jackpotConfettiColors = [
  '#ff1493', '#ff8c00', '#ffd700', '#00e5ff',
  '#8a2be2', '#00ff66', '#ff2d2d', '#ffffff',
];
const jackpotConfetti = Array.from({ length: 40 }, (_, i) => ({
  left: `${(i * 37) % 100}%`,
  color: jackpotConfettiColors[i % jackpotConfettiColors.length],
  delay: `${((i * 13) % 70) / 10}s`,
  dur: `${5 + ((i * 7) % 40) / 10}s`,
  size: `${10 + (i % 5) * 4}px`,
  round: i % 3 === 0,
}));

function App() {

  const [currentStatus, setCurrentStatus] = useState('DEFAULT');
  const [shifts, setShifts] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [scanLog, setScanLog] = useState([]);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [scanIndicator, setScanIndicator] = useState(false);
  const [jackpotCount, setJackpotCount] = useState(0);
  const shiftsListRef = useRef(null);

  useEffect(() => {
    if (currentStatus !== 'SHOW_SHIFTS' || !shifts) return;
    const el = shiftsListRef.current;
    if (!el) return;

    let rafId;
    let startTs = null;

    const run = () => {
      const overflow = el.scrollHeight - el.clientHeight;
      if (overflow <= 0) {
        el.scrollTop = 0;
        return;
      }
      const scrollStart = SHOW_SHIFTS_DURATION * 0.1;
      const scrollEnd = SHOW_SHIFTS_DURATION * 0.85;

      const step = (ts) => {
        if (startTs === null) startTs = ts;
        const elapsed = ts - startTs;
        const t = Math.min(Math.max((elapsed - scrollStart) / (scrollEnd - scrollStart), 0), 1);
        el.scrollTop = overflow * t;
        if (elapsed < SHOW_SHIFTS_DURATION) {
          rafId = requestAnimationFrame(step);
        }
      };
      rafId = requestAnimationFrame(step);
    };

    // ein Frame warten, bis das Layout steht
    rafId = requestAnimationFrame(run);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (el) el.scrollTop = 0;
    };
  }, [currentStatus, shifts]);

  useEffect(() => {
    window.setTimeout(() => {
      if (currentStatus === 'DEFAULT') {
        window.location.reload(true);
      }
    }, 600000);
  }, []);

  // Add keyboard shortcut for toggling debug mode (Ctrl+D)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'd') {
        event.preventDefault();
        setDebugMode(prevMode => !prevMode);
        console.log('Debug mode:', !debugMode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [debugMode]);

  const handleScan = (code) => {
    // If debug mode is enabled, process through debug handler
    if (debugMode) {
      // If the debug handler returns false, ignore this scan
      if (!handleDebugScan(code)) {
        return;
      }
    }

    const totalScans = parseInt(localStorage.getItem('jackpotScanCount') || '0', 10) + 1;
    localStorage.setItem('jackpotScanCount', String(totalScans));
    if (totalScans % JACKPOT_INTERVAL === 0) {
      setJackpotCount(totalScans);
      setShifts(null);
      setCurrentStatus('JACKPOT');
      startTimeout(12000);
      return;
    }

    setCurrentStatus('LOADING');
    setShifts(null);
    axios.get(`${config.server.endpoint}/self-service/shifts/${code}`)
      .then((response) => {
        if (!response.data.success) {
          setCurrentStatus('ERROR');
          startTimeout(5000);
        } else {
          const data = response.data.data;
          if (!data) {
            setCurrentStatus('NOT_FOUND');
            startTimeout(5000);
          } else {
            setCurrentStatus('SHOW_SHIFTS');
            startTimeout();
            // Sort shifts by startAt date
            const sortedData = [...data].sort((a, b) => 
              new Date(a.startAt) - new Date(b.startAt)
            );
            setShifts(sortedData);
          }
        }
      })
      .catch(() => {
        startTimeout(5000);
        setCurrentStatus('ERROR');
      })
  }

  // Debug handler for scans
  const handleDebugScan = (code) => {
    const now = new Date();
    const timeSinceLastScan = lastScanTime ? now - lastScanTime : null;

    console.log(`[DEBUG] Scan detected: ${code}`);
    if (timeSinceLastScan) {
      console.log(`[DEBUG] Time since last scan: ${timeSinceLastScan}ms`);
    }

    // Add to scan log
    setScanLog(prevLog => {
      const newLog = [
        { 
          code, 
          timestamp: now, 
          timeSinceLastScan,
          // Flag scans that come too quickly but don't block them
          tooFast: timeSinceLastScan && timeSinceLastScan < 1000
        }, 
        ...prevLog
      ].slice(0, 20); // Keep only the last 20 scans
      return newLog;
    });

    // Visual indicator
    setScanIndicator(true);
    setTimeout(() => setScanIndicator(false), 500);

    // Update last scan time
    setLastScanTime(now);

    // Log if scans are coming too fast, but don't block them
    if (timeSinceLastScan && timeSinceLastScan < 1000) {
      console.log(`[DEBUG] Fast scan detected (${timeSinceLastScan}ms) - still processing`);
    }

    // Always return true to allow all scans to be processed
    return true;
  };

  const startTimeout = (time) => {
    if (!time) time = SHOW_SHIFTS_DURATION;
    window.setTimeout(() => {
      setCurrentStatus('DEFAULT');
      setShifts(null);
    }, time);
  }

  return (
    <div className="App">
      <CodeReader onScan={handleScan}/>
      {/* Scan indicator - flashes when a scan is detected */}
      {scanIndicator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 0, 0.2)',
          zIndex: 9999,
          pointerEvents: 'none'
        }} />
      )}

      {/* Debug panel */}
      {debugMode && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '300px',
          maxHeight: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: '#00ff00',
          padding: '10px',
          fontFamily: 'monospace',
          fontSize: '12px',
          zIndex: 10000,
          overflowY: 'auto'
        }}>
          <h3 style={{ color: '#00ff00', margin: '0 0 10px 0' }}>DEBUG MODE</h3>
          <p>Press Ctrl+D to toggle debug mode</p>
          <p>NFC scans: {scanLog.length}</p>
          <p>Last scan: {lastScanTime ? new Date(lastScanTime).toLocaleTimeString() : 'None'}</p>

          <h4 style={{ color: '#00ff00', margin: '10px 0 5px 0' }}>Manual Input:</h4>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <input 
              type="text" 
              placeholder="Enter code manually"
              style={{
                flex: 1,
                backgroundColor: '#333',
                color: '#00ff00',
                border: '1px solid #00ff00',
                padding: '5px',
                fontFamily: 'monospace'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = e.target.value.trim();
                  if (value) {
                    handleScan(value);
                    e.target.value = '';
                  }
                }
              }}
            />
          </div>

          <h4 style={{ color: '#00ff00', margin: '10px 0 5px 0' }}>Scan Log:</h4>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {scanLog.map((scan, index) => (
              <div key={index} style={{ 
                borderBottom: '1px solid #333', 
                padding: '5px 0',
                marginBottom: '5px'
              }}>
                <div>Code: <strong>{scan.code}</strong></div>
                <div>Time: {new Date(scan.timestamp).toLocaleTimeString()}</div>
                {scan.timeSinceLastScan && (
                  <div style={{ 
                    color: scan.tooFast ? '#ff6666' : '#00ff00' 
                  }}>
                    Delay: {scan.timeSinceLastScan}ms
                    {scan.tooFast ? ' (fast scan - still processed)' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jackpot easter egg */}
      {currentStatus === 'JACKPOT' && (
        <div className="jackpot">
          <div className="jackpot-rain">
            {jackpotConfetti.map((c, i) => (
              <span
                key={i}
                className={`confetti${c.round ? ' round' : ''}`}
                style={{
                  left: c.left,
                  width: c.size,
                  height: c.size,
                  background: c.color,
                  animationDelay: c.delay,
                  animationDuration: c.dur,
                }}
              />
            ))}
          </div>
          <div className="jackpot-content">
            <div className="jackpot-title">JACKPOT</div>
            <div className="jackpot-reels">
              <span className="reel">1</span>
              <span className="reel">6</span>
              <span className="reel">1</span>
            </div>
            <div className="jackpot-count">
              {jackpotCount}. SCAN SEIT DEM LETZTEN JACKPOT
            </div>
            <div className="jackpot-lose">
              DU HAST <span>NICHTS</span> GEWONNEN!
            </div>
            <div className="jackpot-sub">YOU WON ABSOLUTELY NOTHING</div>
            <div className="jackpot-tip">
              Bessere Chancen beim nächsten Scan… vielleicht
            </div>
          </div>
        </div>
      )}

      <div className={`bg${currentStatus === 'SHOW_SHIFTS' ? ' in' : ''}`} style={{backgroundImage: `url(${gfx_bg})`}}/>
      <img src={gfx_map} alt="" className={`map${currentStatus === 'SHOW_SHIFTS' ? ' in' : ''}`}/>
      <div className={`content-pane${currentStatus === 'SHOW_SHIFTS' ? ' in' : ''}`}>
        <div>
          <h2>
            SUPPORTER:INNEN&nbsp;SCHICHTAUSKUNFT
          </h2>
        </div>
        <div>
          <h4>
            SUPPORTER SHIFTINFORMATION
          </h4>
        </div>
        <div className={`instruction${currentStatus === 'SHOW_SHIFTS' ? ' out' : ''}`}>
          {currentStatus === 'NOT_FOUND' ? (
            <div className="error-message">
              BÄNDSEL&nbsp;NICHT&nbsp;GEFUNDEN<br/>
              WRITSBAND NOT FOUND
            </div>
          ) : null}
          {currentStatus === 'ERROR' ? (
            <div className="error-message">
              SERVERFEHLER!<br/>
              SERVER ERROR!
            </div>
          ) : null}
          <div>
            <h3>
              BITTE HALTE DEIN BÄNDSEL UNTER DEN SCANNER
            </h3>
          </div>
          <div>
            <h5>
              PLEASE HOLD YOUR WRISTBAND UNDER THE SCANNER
            </h5>
          </div>
          <img src={gfx_wristband} alt="" className="wristband"/>
        </div>
        <div className={`shifts${currentStatus === 'SHOW_SHIFTS' ? ' in' : ''}`}>
          <div>
            <h3>
              DEINE SCHICHTEN
            </h3>
          </div>
          <div>
            <h5>
              YOUR SHIFTS
            </h5>
          </div>
          <div className="shifts-list" ref={shiftsListRef}>
            {shifts?.map((shift, sI) => (
              <div className="shift" key={sI}>
                <div className="number">{sI + 1}</div>
                <div className="info">
                  <b>
                    {moment(shift.startAt).subtract(30, 'minute').locale('en').format('ddd')}, {moment(shift.startAt).subtract(30, 'minute').format('DD.MM.')}
                  </b>
                  <span className="spacer"/>//<span className="spacer"/>{moment(shift.startAt).subtract(30, 'minute').format('HH:mm')}
                </div>
                {shift.waitingSpot ? (
                  <div className="location">
                    <img src={gfx_location} alt=""/>
                    {waitingSpot[shift.waitingSpot]}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
